import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Sparkles, Loader2, X, Coins, ArrowLeft, Pencil, Trash2,
    ChevronRight, Wand2, Maximize2, Check, Undo2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import PhotoAnnotator from "@/components/PhotoAnnotator";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import PhotoHistoryStrip from "@/components/PhotoHistoryStrip";
import CreditsTopUpModal from "@/components/CreditsTopUpModal";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal that lets a makelaar / MM apply AI tools (Retouch / Upscale)
 * directly to a delivered project photo. Same editor experience as the
 * standalone `/tools/photo-retouch` page: photo → draw/comment/preset →
 * submit → server replaces the photo in `ai_photo_urls[i]`.
 *
 * Two views:
 *   - "grid": photo grid with a single ✨ button per photo
 *   - "editor": full editor for one selected photo (annotator + chips + comment + model + submit)
 */
export default function AgentPhotoToolsModal({
    open,
    onClose,
    requestId,
    photos = [],
    initialIndex = null,
    onRefetch,
}) {
    const { user, refreshUser } = useAuth();
    const [toolsState, setToolsState] = useState(null);

    // grid view ↔ editor view
    const [editingIdx, setEditingIdx] = useState(null); // number | null
    const [refreshKey, setRefreshKey] = useState(0); // bumps photo thumbnails on completion

    // editor state (per photo)
    const [tool, setTool] = useState("photo_retouch"); // "photo_retouch" | "upscale"
    const [selectedAction, setSelectedAction] = useState(null);
    const [comment, setComment] = useState("");
    const [model, setModel] = useState("nano-banana-pro");
    const [annotating, setAnnotating] = useState(false);
    const [overlayDataUrl, setOverlayDataUrl] = useState(null);
    const [overlayResetSignal, setOverlayResetSignal] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [activeJob, setActiveJob] = useState(null);
    const [reviewJob, setReviewJob] = useState(null); // { jobId, sourceUrl, resultUrl } — shown after completion
    // History-aware state — the active URL and the genuine original come from the strip
    const [activeUrl, setActiveUrl] = useState(null);
    const [originalUrl, setOriginalUrl] = useState(null);
    const imgRef = useRef(null);
    const pollRef = useRef(null);
    const historyRef = useRef(null);

    const balance = toolsState?.credits_balance ?? user?.credits_balance ?? 0;
    const isAdminUser = !!(toolsState?.is_admin || user?.role === "admin");
    const [topUpOpen, setTopUpOpen] = useState(false);
    const access = toolsState?.tool_access || user?.tool_access || {};
    const canRetouch = !!access.photo_retouch;
    const canUpscale = !!access.upscale;
    const quickActions = useMemo(
        () => toolsState?.quick_actions || [],
        [toolsState],
    );
    const retouchModels = toolsState?.retouch_models || [];

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        api.get("/tools/me").then((r) => {
            if (!cancelled) setToolsState(r.data);
            if (!cancelled && r.data?.retouch_models?.[0]?.key) setModel(r.data.retouch_models[0].key);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [open]);

    // Deep-link: opened via a per-photo "Bewerk" button in the embedded fotoreeks
    useEffect(() => {
        if (open && typeof initialIndex === "number" && photos[initialIndex]) {
            openEditor(initialIndex);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialIndex]);

    // Lock body scroll while open
    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    // Cleanup polling on unmount / close
    useEffect(() => {
        if (!open && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, [open]);

    const openEditor = (idx) => {
        // Credits op? Direct de "Bijtanken?" popup tonen (admins uitgezonderd)
        if (!isAdminUser && balance <= 0) setTopUpOpen(true);
        // Default tool when entering editor: photo_retouch if available, else upscale
        const nextTool = canRetouch ? "photo_retouch" : canUpscale ? "upscale" : "photo_retouch";
        setTool(nextTool);
        setSelectedAction(null);
        setComment("");
        setOverlayDataUrl(null);
        setOverlayResetSignal((x) => x + 1);
        setAnnotating(false);
        setActiveJob(null);
        setReviewJob(null);
        setSubmitting(false);
        // Seed with the current `photos[idx]`; the strip will refine with the genuine original after fetching history
        setActiveUrl(photos[idx] || null);
        setOriginalUrl(null);
        setEditingIdx(idx);
    };

    const closeEditor = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        setEditingIdx(null);
        setActiveJob(null);
        setReviewJob(null);
        setActiveUrl(null);
        setOriginalUrl(null);
        setSubmitting(false);
    };

    const handleHistoryChanged = (newActive, newOriginal) => {
        setActiveUrl(newActive);
        if (newOriginal) setOriginalUrl(newOriginal);
        setRefreshKey((k) => k + 1);
        // If we were in review, mark that the user manually picked a different version → close review so editor can continue
        if (reviewJob && newActive !== reviewJob.resultUrl) {
            setReviewJob(null);
        }
        onRefetch?.();
    };

    const uploadOverlayIfNeeded = async () => {
        if (!overlayDataUrl) return null;
        try {
            const blob = await (await fetch(overlayDataUrl)).blob();
            const fd = new FormData();
            fd.append("file", blob, "overlay.png");
            const { data } = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data.url;
        } catch {
            return null;
        }
    };

    const startPolling = (jobId) => {
        if (pollRef.current) clearInterval(pollRef.current);
        const tick = async () => {
            try {
                const { data } = await api.get(`/tools/jobs/${jobId}`);
                setActiveJob(data);
                if (data.status === "completed") {
                    clearInterval(pollRef.current); pollRef.current = null;
                    setSubmitting(false);
                    setRefreshKey((k) => k + 1);
                    // Refresh history strip → pick up new version + new active
                    const hist = await historyRef.current?.reload();
                    if (hist) {
                        setActiveUrl(hist.active_url);
                        setOriginalUrl(hist.original_url);
                    }
                    // Switch to Review view — user picks Approve or Revert
                    setReviewJob({
                        jobId: data.id,
                        sourceUrl: data.source_url,
                        resultUrl: data.result_url,
                    });
                    try { await refreshUser?.({ force: true }); } catch (e) { /* noop */ }
                    onRefetch?.();
                } else if (data.status === "failed") {
                    clearInterval(pollRef.current); pollRef.current = null;
                    setSubmitting(false);
                    toast.error(`Bewerking mislukt: ${data.error || "onbekend"} (credit teruggestort)`);
                    try { await refreshUser?.({ force: true }); } catch (e) { /* noop */ }
                }
            } catch { /* keep polling */ }
        };
        tick();
        pollRef.current = setInterval(tick, 2500);
    };

    const approveReview = () => {
        toast.success(`Foto ${(editingIdx ?? 0) + 1} bewerkt — opgeslagen ✨`);
        closeEditor();
    };

    const revertReview = async () => {
        if (!reviewJob) return;
        try {
            await api.post(
                `/requests/${requestId}/photos/${editingIdx}/revert/${reviewJob.jobId}`,
            );
            setRefreshKey((k) => k + 1);
            // Refresh history strip too (the job is now marked reverted)
            const hist = await historyRef.current?.reload();
            if (hist) {
                setActiveUrl(hist.active_url);
                setOriginalUrl(hist.original_url);
            }
            toast.info(`Origineel hersteld voor foto ${(editingIdx ?? 0) + 1}`);
            onRefetch?.();
            closeEditor();
        } catch (e) {
            const msg = e.response?.data?.detail || "Kon niet herstellen";
            toast.error(typeof msg === "string" ? msg : "Kon niet herstellen");
        }
    };

    const submit = async () => {
        if (editingIdx === null) return;
        if (!isAdminUser && balance <= 0) {
            setTopUpOpen(true);
            return;
        }
        if (tool === "photo_retouch" && !selectedAction && !comment.trim()) {
            toast.error("Kies een snelle actie of typ een opmerking");
            return;
        }
        setSubmitting(true);
        try {
            const overlayUrl = tool === "photo_retouch" ? await uploadOverlayIfNeeded() : null;
            const body = { tool_key: tool };
            if (tool === "photo_retouch") {
                body.action_preset = selectedAction || null;
                body.comment_nl = comment.trim() || null;
                body.overlay_url = overlayUrl;
                body.model = model;
            }
            const { data } = await api.post(
                `/requests/${requestId}/photos/${editingIdx}/ai-tool`,
                body,
            );
            try { await refreshUser?.({ force: true }); } catch (e) { /* noop */ }
            startPolling(data.job_id);
        } catch (e) {
            const msg = e.response?.data?.detail || "Bewerking kon niet starten";
            toast.error(typeof msg === "string" ? msg : "Bewerking kon niet starten");
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex flex-col"
            data-testid="agent-photo-tools-modal"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-6 lg:px-10 h-16 border-b border-white/10 bg-black/60">
                <div className="flex items-center gap-3">
                    {editingIdx !== null ? (
                        <button
                            type="button"
                            onClick={closeEditor}
                            disabled={!!reviewJob}
                            className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="agent-tools-back-btn"
                            title={reviewJob ? "Kies eerst Goedkeuren of Herstellen" : "Terug naar fotoreeks"}
                        >
                            <ArrowLeft className="h-4 w-4" /> {reviewJob ? "Beoordeel resultaat" : "Terug naar fotoreeks"}
                        </button>
                    ) : (
                        <>
                            <Sparkles className="h-5 w-5 text-[#E5A93D]" />
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#E5A93D]">AI Tools — jouw project</p>
                                <h2 className="font-display text-lg font-bold leading-tight">Bewerk je foto&apos;s</h2>
                            </div>
                        </>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <div className="hidden md:flex items-center gap-2 px-3 h-9 rounded-sm bg-[#22c55e]/[0.08] border border-[#22c55e]/30">
                        <Coins className="h-4 w-4 text-[#22c55e]" />
                        <span className="text-sm font-bold tabular-nums text-[#22c55e]" data-testid="modal-credits-balance">{isAdminUser ? "∞" : balance}</span>
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500">credits</span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-9 w-9 flex items-center justify-center bg-zinc-100 hover:bg-zinc-200 rounded-sm transition active:scale-95"
                        data-testid="agent-tools-close-btn"
                        aria-label="Sluiten"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto px-6 lg:px-10 py-8">
                {editingIdx === null ? (
                    <GridView
                        photos={photos}
                        refreshKey={refreshKey}
                        onPick={openEditor}
                        balance={isAdminUser ? Infinity : balance}
                        onTopUp={() => setTopUpOpen(true)}
                    />
                ) : (
                    <>
                        {/* History strip — visible in both Editor and Review */}
                        <div className="mb-6">
                            <PhotoHistoryStrip
                                ref={historyRef}
                                requestId={requestId}
                                photoIdx={editingIdx}
                                refreshKey={refreshKey}
                                onActiveChanged={handleHistoryChanged}
                            />
                        </div>
                        {reviewJob ? (
                            <ReviewView
                                photoIdx={editingIdx}
                                originalUrl={originalUrl || reviewJob.sourceUrl}
                                resultUrl={activeUrl || reviewJob.resultUrl}
                                onApprove={approveReview}
                                onRevert={revertReview}
                            />
                        ) : (
                            <EditorView
                                photoUrl={activeUrl || photos[editingIdx]}
                                photoIdx={editingIdx}
                                refreshKey={refreshKey}
                                tool={tool}
                                setTool={setTool}
                                canRetouch={canRetouch}
                                canUpscale={canUpscale}
                                quickActions={quickActions}
                                selectedAction={selectedAction}
                                setSelectedAction={setSelectedAction}
                                comment={comment}
                                setComment={setComment}
                                retouchModels={retouchModels}
                                model={model}
                                setModel={setModel}
                                annotating={annotating}
                                setAnnotating={setAnnotating}
                                overlayResetSignal={overlayResetSignal}
                                resetOverlay={() => { setOverlayDataUrl(null); setOverlayResetSignal((x) => x + 1); }}
                                onOverlayChange={setOverlayDataUrl}
                                imgRef={imgRef}
                                submitting={submitting}
                                activeJob={activeJob}
                                onSubmit={submit}
                            />
                        )}
                    </>
                )}
            </div>
            <CreditsTopUpModal open={topUpOpen} onClose={() => setTopUpOpen(false)} />
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────
// Grid view — list of photos with ✨ to enter the editor
// ────────────────────────────────────────────────────────────────────────
function GridView({ photos, refreshKey, onPick, balance, onTopUp }) {
    return (
        <>
            <p className="text-sm text-zinc-500 mb-6 max-w-2xl">
                Klik op het ✨ icoon van een foto om de editor te openen. Elke bewerking kost{" "}
                <span className="text-[#22c55e] font-bold">1 credit</span>. Het resultaat vervangt de originele foto — het origineel blijft beschikbaar in de Vergelijk-weergave.
            </p>

            {photos.length === 0 ? (
                <div className="text-center text-sm text-zinc-500 py-20" data-testid="agent-tools-empty">
                    Nog geen foto&apos;s in de fotoreeks van dit project.
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {photos.map((url, i) => {
                        // Use a small thumb for /api/uploads/ paths; for absolute
                        // (R2 presigned) URLs use the source URL untouched so the
                        // signature stays intact.
                        const isAbs = /^https?:\/\//i.test(url || "");
                        let cacheBusted;
                        if (isAbs) {
                            cacheBusted = url;
                        } else {
                            const clean = (url || "").split("?")[0];
                            const m = /\/api\/uploads\/([^/?#]+)$/.exec(clean);
                            const base = m ? `/api/thumb/${m[1]}?w=500` : clean;
                            cacheBusted = `${base}${base.includes("?") ? "&" : "?"}rk=${refreshKey}`;
                        }
                        return (
                            <button
                                key={i}
                                type="button"
                                onClick={() => onPick(i)}
                                className="relative group bg-[#FFFFFF] border border-zinc-200 hover:border-[#E5A93D]/60 rounded-sm overflow-hidden text-left transition-colors"
                                data-testid={`agent-tool-photo-${i}`}
                            >
                                <img
                                    src={cacheBusted}
                                    alt={`Foto ${i + 1}`}
                                    loading="lazy"
                                    className="w-full aspect-square object-cover transition-transform group-hover:scale-[1.02]"
                                />
                                <span className="absolute top-2 left-2 inline-flex items-center justify-center h-6 min-w-[24px] px-1.5 text-[10px] font-bold uppercase tracking-wider bg-black/70 text-white border border-white/10 rounded-sm">
                                    {i + 1}
                                </span>
                                <span
                                    data-testid={`agent-tool-trigger-${i}`}
                                    className="absolute top-2 right-2 inline-flex items-center justify-center h-9 w-9 rounded-sm bg-[#E5A93D] text-black shadow-lg group-hover:bg-[#C8922C] group-active:scale-95 transition"
                                >
                                    <Sparkles className="h-4 w-4" />
                                </span>
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <p className="text-[11px] uppercase tracking-wider text-[#E5A93D] font-semibold">Bewerk deze foto</p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {balance === 0 && (
                <div className="mt-8 p-4 bg-[#E5A93D]/[0.06] border border-[#E5A93D]/30 rounded-sm text-sm text-[#E5A93D] inline-flex items-center gap-3 flex-wrap" data-testid="agent-tools-no-credits">
                    <span className="inline-flex items-center gap-2"><Coins className="h-4 w-4" /> Geen credits meer.</span>
                    <button
                        type="button"
                        onClick={onTopUp}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black text-xs font-bold transition active:scale-95"
                        data-testid="agent-tools-topup-btn"
                    >
                        <Sparkles className="h-3.5 w-3.5" /> Koop credits
                    </button>
                </div>
            )}
        </>
    );
}

// ────────────────────────────────────────────────────────────────────────
// Editor view — annotator + quick actions + comment + model + submit
// ────────────────────────────────────────────────────────────────────────
function EditorView({
    photoUrl, photoIdx, refreshKey,
    tool, setTool, canRetouch, canUpscale,
    quickActions, selectedAction, setSelectedAction,
    comment, setComment,
    retouchModels, model, setModel,
    annotating, setAnnotating,
    overlayResetSignal, resetOverlay, onOverlayChange,
    imgRef, submitting, activeJob, onSubmit,
}) {
    // Resolve a clean image URL — strip cache-busters from server-side URLs
    // Photos can be either:
    //   • Relative `/api/uploads/foo.jpg` → prepend BACKEND, safe to strip our own ?rk= param
    //   • Absolute `https://...r2.cloudflarestorage.com/foo.jpg?X-Amz-Signature=...`
    //     (R2 presigned) → use as-is. Stripping the query string would remove
    //     the AWS signature and the request would 401.
    const raw = photoUrl || "";
    const isAbsolute = /^https?:\/\//i.test(raw);
    let photoSrc;
    if (isAbsolute) {
        // Keep signed query intact; refreshKey only matters for cache-busting
        // our own URLs (R2 returns a fresh URL each time anyway).
        photoSrc = raw;
    } else {
        const clean = raw.split("?")[0];
        photoSrc = `${BACKEND}${clean}?rk=${refreshKey}`;
    }

    const submitDisabled = submitting
        || (tool === "photo_retouch" && !selectedAction && !comment.trim());

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6" data-testid="agent-tools-editor">
            {/* LEFT — Photo + annotator */}
            <div className="relative bg-white border border-zinc-200 rounded-sm overflow-hidden min-h-[480px] flex items-center justify-center">
                <div className="relative inline-block max-w-full p-3">
                    <img
                        ref={imgRef}
                        src={photoSrc}
                        alt={`Foto ${photoIdx + 1}`}
                        className="block max-h-[640px] max-w-full object-contain"
                        data-testid="agent-editor-source-image"
                    />
                    {tool === "photo_retouch" && (
                        <PhotoAnnotator
                            imageRef={imgRef}
                            active={annotating}
                            onChange={onOverlayChange}
                            resetSignal={overlayResetSignal}
                        />
                    )}
                </div>

                {tool === "photo_retouch" && (
                    <div className="absolute top-3 right-3 flex items-center gap-2">
                        <Button
                            size="sm"
                            onClick={() => setAnnotating((v) => !v)}
                            data-testid="agent-editor-annotate-btn"
                            className={`rounded-sm h-8 ${annotating ? "bg-[#E5A93D] text-black hover:bg-[#E5A93D]/90" : "bg-zinc-100 text-black hover:bg-zinc-200"}`}
                        >
                            <Pencil className="h-3.5 w-3.5 mr-1.5" />
                            {annotating ? "Klaar" : "Tekenen"}
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={resetOverlay}
                            data-testid="agent-editor-reset-overlay-btn"
                            className="rounded-sm h-8 text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100"
                        >
                            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Wis tekening
                        </Button>
                    </div>
                )}

                {submitting && (
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center" data-testid="agent-editor-busy">
                        <Loader2 className="h-8 w-8 animate-spin text-[#E5A93D] mb-3" />
                        <p className="text-sm text-white">
                            {activeJob?.status === "processing" ? "AI is bezig…" : "Bezig met versturen…"}
                        </p>
                        <p className="text-xs text-zinc-400 mt-1">Dit duurt 30-90 seconden.</p>
                    </div>
                )}
            </div>

            {/* RIGHT — Controls */}
            <div className="space-y-5">
                {/* Tool tabs */}
                <div className="flex gap-2 p-1 bg-white border border-zinc-200 rounded-sm">
                    <button
                        type="button"
                        disabled={!canRetouch}
                        onClick={() => setTool("photo_retouch")}
                        data-testid="agent-editor-tab-retouch"
                        className={`flex-1 inline-flex items-center justify-center gap-2 h-9 rounded-sm text-sm transition-colors ${
                            tool === "photo_retouch"
                                ? "bg-[#E5A93D] text-black font-semibold"
                                : `text-zinc-500 hover:text-zinc-950 ${canRetouch ? "" : "opacity-40 cursor-not-allowed"}`
                        }`}
                    >
                        <Wand2 className="h-3.5 w-3.5" />
                        AI Retouch
                    </button>
                    <button
                        type="button"
                        disabled={!canUpscale}
                        onClick={() => setTool("upscale")}
                        data-testid="agent-editor-tab-upscale"
                        className={`flex-1 inline-flex items-center justify-center gap-2 h-9 rounded-sm text-sm transition-colors ${
                            tool === "upscale"
                                ? "bg-[#7C3AED] text-white font-semibold"
                                : `text-zinc-500 hover:text-zinc-950 ${canUpscale ? "" : "opacity-40 cursor-not-allowed"}`
                        }`}
                    >
                        <Maximize2 className="h-3.5 w-3.5" />
                        Upscale
                    </button>
                </div>

                {tool === "photo_retouch" ? (
                    <>
                        <div>
                            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Snelle acties</p>
                            <div className="grid grid-cols-2 gap-2">
                                {quickActions.map((a) => (
                                    <button
                                        key={a.key}
                                        type="button"
                                        onClick={() => setSelectedAction((cur) => cur === a.key ? null : a.key)}
                                        data-testid={`agent-editor-quick-action-${a.key}`}
                                        className={`text-sm h-10 px-3 rounded-sm border transition-all text-left ${
                                            selectedAction === a.key
                                                ? "bg-[#E5A93D] text-black border-[#E5A93D] font-medium"
                                                : "bg-white text-zinc-950 border-zinc-200 hover:border-zinc-400"
                                        }`}
                                    >
                                        {a.label}
                                    </button>
                                ))}
                            </div>
                            {selectedAction && (
                                <button
                                    type="button"
                                    onClick={() => setSelectedAction(null)}
                                    className="mt-2 text-[11px] text-zinc-500 hover:text-zinc-950"
                                >
                                    Selectie wissen
                                </button>
                            )}
                        </div>

                        <div>
                            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
                                Extra opmerking <span className="text-zinc-400">(optioneel)</span>
                            </p>
                            <Textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Tip: Wees gedetailleerd in je omschrijving voor betere resultaten."
                                rows={3}
                                data-testid="agent-editor-comment-input"
                                className="bg-white border-zinc-200 text-sm resize-none"
                            />
                            <p className="mt-1.5 text-[11px] text-zinc-500">
                                Of teken met de rode pen op de foto om aan te duiden wat moet veranderen.
                            </p>
                        </div>
                    </>
                ) : (
                    <div className="p-4 bg-[#7C3AED]/[0.12] border border-[#7C3AED]/40 rounded-sm">
                        <p className="text-sm text-zinc-100">
                            Maakt deze foto scherper en hoger resolutie. Geen prompt nodig — de AI gebruikt de bron als input.
                        </p>
                        <p className="mt-2 text-[11px] text-zinc-400">
                            Kost <span className="text-[#22c55e] font-bold">1 credit</span> · duurt ±60s.
                        </p>
                    </div>
                )}

                <Button
                    onClick={onSubmit}
                    disabled={submitDisabled}
                    data-testid="agent-editor-submit-btn"
                    className={`w-full font-medium rounded-sm h-11 ${
                        tool === "photo_retouch"
                            ? "bg-[#E5A93D] hover:bg-[#E5A93D]/90 text-black"
                            : "bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white"
                    }`}
                >
                    {submitting ? (
                        <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Bezig…</>
                    ) : (
                        <><Sparkles className="h-4 w-4 mr-2" /> Genereer (1 credit)</>
                    )}
                </Button>
            </div>
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────
// Review view — Before/After slider with Approve / Revert buttons.
// Shown automatically right after a tool job completes.
// ────────────────────────────────────────────────────────────────────────
function ReviewView({ photoIdx, originalUrl, resultUrl, onApprove, onRevert }) {
    const toAbs = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);
    const beforeUrl = toAbs(originalUrl);
    const afterUrl = toAbs(resultUrl);

    return (
        <div className="max-w-5xl mx-auto" data-testid="agent-tools-review">
            <div className="text-center mb-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#22c55e]">Klaar — beoordeel het resultaat</p>
                <h3 className="font-display text-2xl font-bold mt-1.5">Origineel &amp; actieve versie — foto {photoIdx + 1}</h3>
                <p className="text-sm text-zinc-500 mt-2 max-w-xl mx-auto">
                    Sleep de schuif om het origineel met de gekozen versie te vergelijken. Wil je een andere versie? Klik op een miniatuur bovenaan. Tevreden? <span className="text-zinc-950 font-medium">Goedkeuren</span> bewaart de actieve versie definitief.
                </p>
            </div>

            <div className="bg-white border border-zinc-200 rounded-sm p-4 lg:p-6 flex items-center justify-center">
                <BeforeAfterSlider
                    beforeUrl={beforeUrl}
                    afterUrl={afterUrl}
                    alt={`Vergelijking foto ${photoIdx + 1}`}
                    className="max-h-[64vh]"
                    testId="agent-review-slider"
                />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                    onClick={onRevert}
                    variant="ghost"
                    data-testid="agent-review-revert-btn"
                    className="h-12 px-6 rounded-sm bg-white hover:bg-zinc-100 border border-zinc-200 text-zinc-950 inline-flex items-center gap-2"
                >
                    <Undo2 className="h-4 w-4" />
                    Herstel vorige
                </Button>
                <Button
                    onClick={onApprove}
                    data-testid="agent-review-approve-btn"
                    className="h-12 px-8 rounded-sm bg-[#22c55e] hover:bg-[#16a34a] text-black font-bold inline-flex items-center gap-2"
                >
                    <Check className="h-5 w-5" />
                    Goedkeuren &amp; opslaan
                </Button>
            </div>

            <p className="mt-4 text-center text-[11px] text-zinc-500">
                Tip: alle versies blijven bewaard — je kunt later altijd terugkeren naar een eerdere bewerking.
            </p>
        </div>
    );
}
